import { useLocation } from "wouter";
import { Truck, ShieldAlert, Check, ExternalLink, ShieldCheck, Award } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import ServicePage from "./ServicePage";
import { serviceData } from "./serviceData";
import NotFound from "../not-found";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
} satisfies Variants;

const einbruchschutzTrustSection = (
  <section className="py-14 md:py-20 bg-zinc-50 border-y border-zinc-100">
    <div className="container mx-auto px-4 md:px-6 max-w-4xl">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      >
        <motion.p variants={fadeUp} className="text-primary font-bold tracking-wider uppercase text-sm mb-3">
          Fachkompetenz & Nachweise
        </motion.p>
        <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-zinc-900 mb-4">
          Geprüfte Fachkompetenz im Einbruchschutz
        </motion.h2>
        <motion.p variants={fadeUp} className="text-zinc-600 text-base leading-relaxed mb-8 max-w-2xl">
          Im Bereich Einbruchschutz sind fachgerechte Beratung und Montage entscheidend. Deshalb setzen wir auf geprüfte Qualität, anerkannte Nachweise und individuelle Lösungen – abgestimmt auf Ihr Gebäude und Ihre Anforderungen.
        </motion.p>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Bulletpoints */}
          <motion.ul variants={fadeUp} className="space-y-4">
            {[
              "Seit 2005 im Bereich Einbruchschutz tätig",
              "Eingetragener Fachbetrieb im Errichternachweis der Polizei Baden-Württemberg",
              "Spezialisierung auf mechanische Sicherungseinrichtungen",
              "Zertifiziert nach DIN EN ISO 9001 (DEKRA)",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-zinc-100 shadow-sm">
                <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-zinc-700 font-medium">{item}</span>
              </li>
            ))}
          </motion.ul>

          {/* Erklärungstext + Nachweise */}
          <motion.div variants={fadeUp} className="space-y-5">
            <p className="text-zinc-600 text-base leading-relaxed">
              Unser Betrieb ist im offiziellen Errichternachweis der Polizei Baden-Württemberg gelistet.
              Damit erfüllen wir die Anforderungen an fachgerechte Beratung und Montage im Bereich Einbruchschutz.
            </p>
            <p className="text-zinc-600 text-base leading-relaxed">
              Als ISO 9001-zertifizierter Fachbetrieb arbeiten wir nach geprüften Qualitätsprozessen –
              für verlässliche und fachgerechte Ergebnisse bei jedem Auftrag.
            </p>

            {/* Nachweis-Badges */}
            <div className="space-y-3 pt-1">
              <a
                href="https://www.k-einbruch.de/fachbetriebssuche/detail/193-joachim-britz/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-zinc-200 hover:border-primary/30 hover:shadow-sm transition-all duration-200"
              >
                <div className="w-9 h-9 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4.5 h-4.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-primary tracking-wider uppercase leading-none mb-0.5">Polizei Baden-Württemberg</p>
                  <p className="text-sm text-zinc-700 font-medium truncate">Errichternachweis K-Einbruch</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>

              <a
                href="/zertifikate"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-zinc-200 hover:border-primary/30 hover:shadow-sm transition-all duration-200"
              >
                <div className="w-9 h-9 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center shrink-0">
                  <Award className="w-4.5 h-4.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-primary tracking-wider uppercase leading-none mb-0.5">DEKRA Certification GmbH</p>
                  <p className="text-sm text-zinc-700 font-medium truncate">ISO 9001:2015 zertifiziert</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default function ServicePageWrapper() {
  const [location] = useLocation();
  const data = serviceData.find((s) => s.route === location);

  if (!data) return <NotFound />;

  let heroIcon: React.ReactNode | undefined;
  if (data.useIcon === "truck") heroIcon = <Truck className="w-16 h-16 text-primary" />;
  if (data.useIcon === "shieldalert") heroIcon = <ShieldAlert className="w-16 h-16 text-primary" />;

  return (
    <ServicePage
      title={data.title}
      subtitle={data.subtitle}
      heroImg={data.heroImg}
      heroIcon={heroIcon}
      description={data.description}
      details={data.details}
      advantages={data.advantages}
      gallery={data.gallery}
      extraSection={location === "/einbruchschutz" ? einbruchschutzTrustSection : undefined}
    />
  );
}
